import React, { useState } from 'react';
import Button from 'antd/lib/button';
import TreeSelect from 'antd/lib/tree-select';
import { DotChartOutlined } from '@ant-design/icons';
import { Badge, ConfigProvider, Modal, Tooltip } from 'antd/lib';
import { getAntdThemeTokens, runwayColors } from '@/components/styles/globalStyles';
import { OrderedStatisticCategory, OrgOverviewAvailableMetric, StatisticCategory } from '@/types';
import { useSidebarColors, useSidebarTheme, useThemeMode } from '@/components/styles/themeMode';
import antdTheme from 'antd/lib/theme';
import { displayTitleForStatCategory } from '@/components/orgOverview/orgOverviewHelpers';

interface OrgOverviewMetricsSelectorProps {
    availableMetrics: OrgOverviewAvailableMetric[];
    selectedMetrics: string[];
    setSelectedMetrics: (value: string[]) => void;
    isLoading?: boolean;
}

export const OrgOverviewMetricsSelector = ({
    availableMetrics,
    selectedMetrics,
    setSelectedMetrics,
    isLoading = false,
}: OrgOverviewMetricsSelectorProps) => {
    const sidebarColors = useSidebarColors();
    const sidebarTheme = useSidebarTheme();

    const [filtersVisible, setFiltersVisible] = useState(false);
    const [draftSelectedMetrics, setDraftSelectedMetrics] = useState<string[]>(selectedMetrics);
    const openModal = () => {
        setDraftSelectedMetrics(selectedMetrics);
        setFiltersVisible(true);
    };
    const closeModal = () => {
        setDraftSelectedMetrics(selectedMetrics);
        setFiltersVisible(false);
    };
    const confirmModal = () => {
        setSelectedMetrics(draftSelectedMetrics);
        setFiltersVisible(false);
    };

    const { mode } = useThemeMode();
    const isDark = mode === 'dark';

    const treeData = React.useMemo(() => {
        const metricsByCategory = availableMetrics.reduce(
            (acc, metric) => {
                if (!metric.displayName) {
                    return acc;
                }

                acc[metric.category] = [...(acc[metric.category] ?? []), metric];
                return acc;
            },
            {} as Record<StatisticCategory, OrgOverviewAvailableMetric[]>,
        );

        return Object.entries(OrderedStatisticCategory)
            .sort((left, right) => left[1] - right[1])
            .map(([category]) => category as StatisticCategory)
            .filter((category) => (metricsByCategory[category] ?? []).length > 0)
            .map((category) => ({
                title: displayTitleForStatCategory(category),
                value: category,
                selectable: false,
                children: [...metricsByCategory[category]]
                    .sort((left, right) => left.type.localeCompare(right.type))
                    .map((metric) => ({
                        title: metric.displayName,
                        value: metric.type,
                        key: metric.type,
                    })),
            }));
    }, [availableMetrics]);

    const metricLabelByType = React.useMemo(
        () =>
            availableMetrics.reduce(
                (acc, metric) => {
                    if (metric.displayName) {
                        acc[metric.type] = metric.displayName;
                    }
                    return acc;
                },
                {} as Record<string, string>,
            ),
        [availableMetrics],
    );

    const appliedFiltersCount = selectedMetrics?.length || 0;

    return (
        <Badge
            count={appliedFiltersCount > 0 ? appliedFiltersCount : null}
            style={{
                backgroundColor: runwayColors.primary,
                borderColor: runwayColors.white,
                color: 'white',
                fontSize: 13,
            }}
        >
            <Button
                icon={<DotChartOutlined style={{ color: runwayColors.primary }} />}
                type="default"
                style={{
                    width: 248,
                    border: `1px solid ${sidebarColors.border}`,
                    backgroundColor: sidebarTheme !== 'light' ? 'transparent' : sidebarColors.dropdownBg,
                }}
                onClick={openModal}
                loading={isLoading}
                disabled={isLoading}
            >
                Select metrics...
            </Button>
            <ConfigProvider
                theme={{
                    algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
                    token: getAntdThemeTokens(isDark ? 'dark' : 'light'),
                }}
            >
                <Modal
                    title="Metrics"
                    open={filtersVisible}
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                    }}
                    onClose={closeModal}
                    onOk={confirmModal}
                    onCancel={closeModal}
                    cancelButtonProps={{ style: { display: 'none' } }}
                >
                    <div
                        style={{
                            paddingLeft: 16,
                            paddingRight: 16,
                            paddingBottom: 18,
                            maxHeight: 450,
                            overflowY: 'scroll',
                        }}
                    >
                        <TreeSelect
                            treeCheckable
                            showSearch
                            style={{ width: '100%' }}
                            value={draftSelectedMetrics}
                            onChange={(values) => setDraftSelectedMetrics(values as string[])}
                            treeData={treeData}
                            placeholder="Select metrics"
                            maxTagCount={5}
                            maxTagPlaceholder={(omittedValues) => {
                                const hiddenMetricLabels = omittedValues
                                    .map((omittedValue) => metricLabelByType[String(omittedValue.value)] ?? null)
                                    .filter((label): label is string => !!label);

                                return (
                                    <Tooltip title={hiddenMetricLabels.join(', ')}>
                                        <span>+{omittedValues.length} metrics...</span>
                                    </Tooltip>
                                );
                            }}
                            allowClear
                            treeDefaultExpandAll
                        />
                    </div>
                </Modal>
            </ConfigProvider>
        </Badge>
    );
};
